import React from 'react';
import { LOGO_MAISON, LOGO_2026, styleDuMasque } from '@/lib/logoDuManoir';

/** The Manoir's ORIGINAL golden blason — the real signage render (glossy gold
 *  escutcheon, interlocked LM, scroll ornaments), transparent PNG, no wordmark
 *  so the « DAUTUN » signage typo never ships. Owner's order (2026-07-11):
 *  the chrome mark IS this original blason doré — never the vector « LM » I
 *  drew — everywhere the shield appears. */
const BLASON_OR = '/manoir/enseigne-or.png';
// The file's own pixels (147×121), declared on the <img> so the browser
// reserves the slot's width from the aspect ratio BEFORE the PNG is decoded.
// Audit of 10/09/2026: sized by `h-[42px] w-auto` alone, the mark was 0 px
// wide until decode, then jumped to 51 px — and the wrapping site bar
// re-flowed the whole page under the visitor (CLS 0.68 per visit).
export const BLASON_OR_WIDTH = 147;
export const BLASON_OR_HEIGHT = 121;

/**
 * ManoirBlason — the Manoir's gold « LM » shield (the ONE brand mark of the
 * app chrome). The `shield` variant renders the owner's ORIGINAL golden
 * blason render (public/manoir/enseigne-or.png) — his real signage, not a
 * recolored or redrawn variant (owner's standing order: « arrêtez de me
 * changer à chaque fois »). Sized by the caller's w/h utilities; `object-
 * contain` keeps the blason's aspect intact inside any slot.
 *
 * Decorative by default (aria-hidden); pass `title` to expose it as the brand
 * image (becomes the img's alt). Any color token in `className` is a harmless
 * no-op on the raster — the blason is always its own champagne gold, which is
 * exactly what the owner asked for (gold on light AND over the film).
 *
 * Props:
 *   className — size (+ optional, now-decorative color) utilities
 *   title     — accessible name; omitted = decorative (aria-hidden)
 *   variant   — 'shield' (default, the chrome mark = the original blason) |
 *               'lockup' (the full engraved signage lockup: shield + flanking
 *               scroll ornaments + dotted rule + « LE MANOIR / D'AUTUN »
 *               wordmark — still drawn as crisp SVG for large sign contexts)
 */
/**
 * Le matte alpha du logo OFFICIEL, à la taille où il s'affiche.
 *
 * ⚠ CE COMMENTAIRE A MENTI JUSQU'AU 15/09/2026. Il disait « il vit ici […] et
 * `SiteHome` le réexporte pour que rien ne se recopie » : `SiteHome` en tenait
 * en réalité sa PROPRE copie, et l'en-tête du site une troisième, pointant sur
 * un autre fichier. Le logo vit désormais dans `lib/logoDuManoir.js`, seul, et
 * ces deux lignes-ci ne sont plus qu'un renvoi gardé par un test.
 */
export const LOGO_MASK_SRC = LOGO_MAISON.src;

export default function ManoirBlason({
    className = 'w-10 h-12 text-manoir-gold',
    title,
    variant = 'shield',
    style,
    'data-testid': testId,
}) {
    // ⚑ `logo` — LE LOGO OFFICIEL, peint depuis son matte, jamais redessiné.
    //
    // ⚠ POURQUOI CETTE VARIANTE EXISTE (15/09/2026) : la page « Bientôt » est
    // partie avec `lockup`, qui est un TRACÉ SVG DE NOTRE CRU — « still drawn
    // as crisp SVG », le dit son propre commentaire. Steven l'a vu tout de
    // suite : « vous avez mis le mauvais logo ». C'est la consigne permanente,
    // enfreinte une seconde fois (la première, un écu vectoriel inventé le
    // 29/07, lui avait fait renvoyer son propre logo).
    //
    // `lockup` garde sa place — une signalétique très grande où un tracé
    // vectoriel reste net. Mais il ne doit plus être le chemin le plus court :
    // *quand la bonne chose est plus difficile que la mauvaise, on refait la
    // faute.* Voilà la bonne chose, aussi facile à écrire.
    // ⚑ `logo2026` — LE LETTRAGE QUE STEVEN A ENVOYÉ LE 15/09, bas-de-casse
    // sur une ligne. Il ne remplace PAS `logo` partout : les deux lockups
    // n'ont pas le même rapport (1,61 contre 1,35), donc l'accueil et
    // l'en-tête du site garderaient une boîte taillée pour l'autre. Ils
    // attendent son arbitrage ; la page « Bientôt », elle, l'a demandé.
    if (variant === 'logo' || variant === 'logo2026') {
        const logo = variant === 'logo2026' ? LOGO_2026 : LOGO_MAISON;
        return (
            <span
                role="img"
                aria-label={title || "Le Manoir d'Autun"}
                data-testid={testId}
                className={className}
                /* ⚠ Le masque d'abord, l'appelant ENSUITE — mais l'appelant ne
                   pose que des dimensions (`boite()`), jamais un `mask-*`.
                   L'ordre est donc sans piège tant que cela reste vrai, et un
                   test le vérifie plutôt que de le supposer. */
                style={{ ...styleDuMasque(logo), ...style }}
            />
        );
    }
    if (variant === 'lockup') {
        return <BlasonLockup className={className} title={title} testId={testId} />;
    }
    return (
        <img
            src={BLASON_OR}
            width={BLASON_OR_WIDTH}
            height={BLASON_OR_HEIGHT}
            className={`${className} object-contain`}
            data-testid={testId}
            alt={title || ''}
            aria-hidden={title ? undefined : 'true'}
            draggable={false}
        />
    );
}

/**
 * The engraved scroll ornament flanking the shield — drawn once, mirrored
 * for the right side (fidelity pass against logo-officiel.jpg): a C-scroll
 * with a curled eye hugging the shield's lower flank, a solid wing-leaf
 * sweeping outward, a small outer curl with trailing dots, and a frond
 * crossing beneath the shield's OPEN point.
 */
function ScrollOrnament() {
    return (
        <g strokeWidth="1">
            {/* Main C-scroll — from the shield's flank down to a curled eye. */}
            <path d="M44 29.5 C37.5 31 33.5 35 34 40.5 C34.4 44 37.6 45.2 39.2 43 C40.2 41.4 38.8 39.6 37.4 40.6" />
            <circle cx="38.3" cy="41.8" r="0.55" fill="currentColor" stroke="none" />
            {/* Solid wing-leaf sweeping outward. */}
            <path fill="currentColor" stroke="none" d="M35.5 45.4 C31.5 43.4 27 43.2 23 44.7 C27 46.4 32 46.5 35.8 45.9 Z" />
            {/* Outer curl with its eye, then trailing engraved dots. */}
            <path strokeWidth="0.9" d="M24.5 40.2 C22 38.8 19.6 39.4 18.4 41.4 C20.6 42.6 23.2 42.3 24.8 40.9" />
            <circle cx="19.6" cy="40.9" r="0.5" fill="currentColor" stroke="none" />
            <circle cx="16.4" cy="42.2" r="0.6" fill="currentColor" stroke="none" />
            <circle cx="14.2" cy="42.9" r="0.45" fill="currentColor" stroke="none" />
            {/* Frond crossing beneath the shield's open point. */}
            <path fill="currentColor" stroke="none" d="M45.5 46.2 C48.5 43 53 41.9 58.3 42.9 C55 45.6 50 46.9 45.9 46.7 Z" />
            {/* Inner hairline echoing the scroll. */}
            <path strokeWidth="0.85" d="M39.5 33 C36 34.4 33.8 37 33.8 40.3" />
        </g>
    );
}

/**
 * BlasonLockup — the full signage lockup, refined against the OFFICIAL flat
 * logo (public/manoir/logo-officiel.jpg, the definitive reference): shield
 * with a gently peaked chief and an OPEN bottom point (the ornaments cross
 * beneath it), crisp double outline, interlocked thin-serif LM, mirrored
 * scroll ornaments with solid wing-leaves, the dotted rule flanked by long
 * rules, « LE MANOIR » near full width and « D'AUTUN » between short rules.
 * currentColor throughout, so it declines gold-on-ivory and ivory-on-navy.
 */
function BlasonLockup({ className = 'w-44 h-36 text-manoir-gold', title, testId }) {
    return (
        <svg
            viewBox="0 0 120 96"
            fill="none"
            className={className}
            data-testid={testId}
            role={title ? 'img' : undefined}
            aria-label={title}
            aria-hidden={title ? undefined : 'true'}
        >
            <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                {/* The shield — peaked chief, open point (the reference's
                    escutcheon breaks where the fronds cross beneath). */}
                <g transform="translate(43 2) scale(0.85)">
                    <path
                        d="M17.2 43 C10.8 39.3 4 34 4 25.5 L4 7 L20 3.6 L36 7 L36 25.5 C36 34 29.2 39.3 22.8 43"
                        strokeWidth="2"
                    />
                    <path
                        d="M18 40.2 C12.6 37 6.9 32.4 6.9 25 L6.9 9.3 L20 6.4 L33.1 9.3 L33.1 25 C33.1 32.4 27.4 37 22 40.2"
                        strokeWidth="0.9"
                    />
                    <text
                        x="20"
                        y="28"
                        textAnchor="middle"
                        fontFamily="'Cormorant Garamond', serif"
                        fontWeight="500"
                        fontSize="18.5"
                        letterSpacing="-1"
                        fill="currentColor"
                        stroke="none"
                    >
                        LM
                    </text>
                </g>

                {/* Flanking scroll ornaments — left, then mirrored right. */}
                <ScrollOrnament />
                <g transform="translate(120 0) scale(-1 1)">
                    <ScrollOrnament />
                </g>

                {/* The dotted rule — dots tapering from the center, flanked
                    by the reference's long rules. */}
                <path d="M6 52 H30 M90 52 H114" strokeWidth="1.2" />
                <g fill="currentColor" stroke="none">
                    <circle cx="60" cy="52" r="2" />
                    <circle cx="55" cy="52" r="1.4" />
                    <circle cx="65" cy="52" r="1.4" />
                    <circle cx="50.6" cy="52" r="1.15" />
                    <circle cx="69.4" cy="52" r="1.15" />
                    <circle cx="46.6" cy="52" r="0.95" />
                    <circle cx="73.4" cy="52" r="0.95" />
                    <circle cx="43" cy="52" r="0.75" />
                    <circle cx="77" cy="52" r="0.75" />
                    <circle cx="39.8" cy="52" r="0.6" />
                    <circle cx="80.2" cy="52" r="0.6" />
                    <circle cx="37" cy="52" r="0.48" />
                    <circle cx="83" cy="52" r="0.48" />
                </g>

                {/* The wordmark. */}
                <text
                    x="60"
                    y="72"
                    textAnchor="middle"
                    fontFamily="'Cormorant Garamond', serif"
                    fontWeight="500"
                    fontSize="17.5"
                    letterSpacing="1.2"
                    fill="currentColor"
                    stroke="none"
                >
                    LE MANOIR
                </text>
                <text
                    x="60"
                    y="86"
                    textAnchor="middle"
                    fontFamily="'Cormorant Garamond', serif"
                    fontWeight="500"
                    fontSize="10"
                    letterSpacing="2.2"
                    fill="currentColor"
                    stroke="none"
                >
                    D&rsquo;AUTUN
                </text>
                <path d="M8 83 H26.5 M93.5 83 H112" strokeWidth="1.1" />
            </g>
        </svg>
    );
}
